import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useOutletContext, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/lib/table';
import { useQuery } from '@tanstack/react-query';
import { Table, Spin, message, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useDebounce } from 'use-debounce';

import DashboardHeader from './DashboardHeader';
import TransactionBalance from './TransactionBalance';
import useTranslation from 'customHooks/translate';
import { getDebitorBalances, getCreditorBalances } from 'services/tabreport';
import styled from 'styled-components';
import { setCulture } from 'services/others';
import { ContextType, DashboardTableData } from 'types';
import { BALANCES } from 'events/types';
import { truncateText } from 'utils';
import AddNewPartnerForm from 'pages/Dialogs/AddNewPartnerForm';

import Statistics from './Statistics';

export const useDashboardContext = () => useOutletContext<ContextType>();

const Dashboard = () => {
  const lang = (localStorage.getItem('language') || 'en').replace(/['"]/g, '');

  useEffect(() => {
    setCulture(lang);
  }, [lang]);

  const translation = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const { pathname, state } = location;
  const isFetchingRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [transactionType, setTransactionType] = useState(
    `${translation.dashboard.PartnerTabDr}`
  );
  const [pageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [partnerName, setPartnerName] = useState('');
  const [debouncedPartnerName] = useDebounce(partnerName, 800);
  const [tableData, setTableData] = useState<DashboardTableData[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [highlightedPartnerId, setHighlightedPartnerId] = useState<
    number | null
  >(null);
  const [addCustomerFormOpen, setAddCustomerFormOpen] = useState(false);
  const [partnerToEdit, setPartnerToEdit] = useState<DashboardTableData | null>(
    null
  );

  const handleOpen = () => setOpen(!open);

  const handleClickCustomer = (record) => {
    localStorage.setItem('highlightPage', pageIndex.toString());
    localStorage.setItem('customerCountryCode', record.countryCode);
    navigate(
      pathname.includes('credit')
        ? `/reports/creditors/${record.id}`
        : `/reports/debitors/${record.id}`
    );
  };

  const renderNameColumn = (value: string, record: DashboardTableData) => {
    const needsTooltip = value.length > 20;

    return (
      <Tooltip title={needsTooltip ? value : ''} placement="topLeft">
        <div
          className="flex h-[60px] cursor-pointer items-center gap-[5px]"
          data-partner-id={record.id}
          onClick={() => {
            handleClickCustomer(record);
          }}
        >
          <p className="text-[14px] font-medium text-[#1D1D1D]">
            {truncateText(value, 20)}
          </p>
          <img
            src="assets/dashboard-table-arrow.svg"
            className="h-[18px] w-[18px]"
            alt=""
          />
        </div>
      </Tooltip>
    );
  };

  const renderBalanceColumn = (value: number) => {
    return (
      <span className="text-[14px] font-normal text-[#1D1D1D]">
        {new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value)}
      </span>
    );
  };

  const [dynamicColumns, setDynamicColumns] = useState<
    ColumnsType<DashboardTableData>
  >([]);

  const handleOpenEditPartner = (record: DashboardTableData) => {
    setPartnerToEdit(record);
    setAddCustomerFormOpen(true);
  };

  useEffect(() => {
    if (tableData.length > 0) {
      const currencySet = new Set<string>();

      tableData.forEach((item) => {
        if (item.balances) {
          Object.keys(item.balances).forEach((key) => currencySet.add(key));
        }
      });

      const newColumns: ColumnsType<DashboardTableData> = [
        {
          title: `${translation.dashboard.PartnerReport_Name}`,
          dataIndex: 'name',
          key: 'name',
          align: 'left',
          ellipsis: true,
          render: renderNameColumn,
          width: 200,
        },
      ];

      Array.from(currencySet).forEach((currency) => {
        newColumns.push({
          title: currency,
          dataIndex: ['balances', currency],
          key: currency,
          align: 'center',
          width: 100,
          render: renderBalanceColumn,
        });
      });

      newColumns.push({
        title: '',
        key: 'actions',
        align: 'center',
        width: 50,
        render: (_, record) => (
          <div>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenEditPartner(record);
              }}
            >
              <EyeOutlined className="h-10 w-full cursor-pointer" />
            </button>
          </div>
        ),
      });

      setDynamicColumns(newColumns);
    }
  }, [
    tableData,
    translation.dashboard.PartnerReport_Name,
    pageIndex,
    pageSize,
    highlightedPartnerId,
  ]);

  useEffect(() => {
    setTransactionType(
      pathname.includes('credit')
        ? translation.dashboard.PartnerTabCr
        : translation.dashboard.PartnerTabDr
    );
  }, [
    pathname,
    translation.dashboard.PartnerTabDr,
    translation.dashboard.PartnerTabCr,
  ]);

  // Effect to handle navigation state and highlight partner
  useEffect(() => {
    // Check both navigation state and localStorage
    let highlightPartner = state?.highlightPartner;
    let scrollToPartner = state?.scrollToPartner;

    // If not in state, check localStorage
    if (!highlightPartner) {
      highlightPartner = localStorage.getItem('highlightPartner');
      scrollToPartner = localStorage.getItem('scrollToPartner') === 'true';
    }

    if (highlightPartner && scrollToPartner && tableData.length > 0) {
      // Remove horizontal scroll first
      setTimeout(() => {
        const tableContainer = tableContainerRef.current;
        if (tableContainer) {
          tableContainer.scrollLeft = 0;
        }
      }, 100);

      // Find the partner in the current data
      const targetPartner = tableData.find(
        (partner) =>
          partner.name.toLowerCase().trim() ===
          highlightPartner.toLowerCase().trim()
      );

      if (targetPartner) {
        setHighlightedPartnerId(targetPartner.id);

        // Scroll to the partner after a short delay to ensure rendering
        setTimeout(() => {
          // Find the highlighted element in DOM
          const highlightedElement = document.querySelector(
            `[data-partner-id="${targetPartner.id}"]`
          ) as HTMLElement;
          if (highlightedElement) {
            // Get the table container
            const tableContainer = tableContainerRef.current;
            if (tableContainer) {
              // Calculate element position relative to container
              const containerRect = tableContainer.getBoundingClientRect();
              const elementRect = highlightedElement.getBoundingClientRect();

              // Calculate scroll position to center the element
              const scrollTop =
                tableContainer.scrollTop +
                (elementRect.top - containerRect.top) -
                containerRect.height / 2;

              // Smooth scroll to the calculated position
              tableContainer.scrollTo({
                top: Math.max(0, scrollTop),
                left: 0,
                behavior: 'smooth',
              });
            }
          }
        }, 800); // Increase delay

        // Clear highlight after 5 seconds (increased from 3)
        setTimeout(() => {
          setHighlightedPartnerId(null);
        }, 5000);

        // Clear localStorage after using
        if (!state?.highlightPartner) {
          localStorage.removeItem('highlightPartner');
          localStorage.removeItem('scrollToPartner');
        }
      }

      // Clear the navigation state to prevent re-highlighting on re-renders
      if (state?.highlightPartner) {
        navigate(pathname, { replace: true, state: {} });
      }
    }
  }, [state, tableData, pathname, navigate]);

  // Separate effect to handle localStorage check when component mounts
  useEffect(() => {
    // Check localStorage on component mount
    const highlightPartner = localStorage.getItem('highlightPartner');
    const scrollToPartner = localStorage.getItem('scrollToPartner') === 'true';
    const timestamp = localStorage.getItem('highlightTimestamp');
    const highlightPage = Number(localStorage.getItem('highlightPage') || 0);

    // Only process recent highlight actions (within 10 seconds)
    const isRecent = timestamp && Date.now() - parseInt(timestamp) < 10000;

    if (highlightPartner && scrollToPartner && isRecent) {
      const preloadAndHighlight = async () => {
        // Fetch pages up to the saved index
        for (let i = 0; i <= highlightPage; i++) {
          await fetchData(i, i === 0, debouncedPartnerName);
        }

        // Wait a short delay for DOM to render fully
        setTimeout(() => {
          const targetPartner = tableData.find(
            (partner) =>
              partner.name.toLowerCase().trim() ===
              highlightPartner.toLowerCase().trim()
          );

          if (targetPartner) {
            setHighlightedPartnerId(targetPartner.id);

            // Scroll to partner row
            const highlightedElement = document.querySelector(
              `[data-partner-id="${targetPartner.id}"]`
            ) as HTMLElement;

            if (highlightedElement && tableContainerRef.current) {
              const container = tableContainerRef.current;
              const containerRect = container.getBoundingClientRect();
              const elementRect = highlightedElement.getBoundingClientRect();

              const scrollTop =
                container.scrollTop +
                (elementRect.top - containerRect.top) -
                containerRect.height / 2;

              container.scrollTo({
                top: Math.max(0, scrollTop),
                behavior: 'smooth',
              });
            }

            // Remove highlight after a few seconds
            setTimeout(() => setHighlightedPartnerId(null), 5000);

            // Cleanup
            localStorage.removeItem('highlightPartner');
            localStorage.removeItem('scrollToPartner');
            localStorage.removeItem('highlightPage');
            localStorage.removeItem('highlightTimestamp');
          }
        }, 800);
      };

      preloadAndHighlight();
    } else if (!isRecent) {
      localStorage.removeItem('highlightPartner');
      localStorage.removeItem('scrollToPartner');
      localStorage.removeItem('highlightPage');
      localStorage.removeItem('highlightTimestamp');
    }
  }, []);

  const fetchData = useCallback(
    async (
      currentPage: number,
      isNewRequest: boolean = false,
      partnerName: string
    ) => {
      if (isFetchingRef.current) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      try {
        const result = pathname.includes('credit')
          ? await getCreditorBalances(pageSize, currentPage, partnerName)
          : await getDebitorBalances(pageSize, currentPage, partnerName);

        const newData = result?.data?.data || [];

        if (isNewRequest) {
          setTableData(newData);
          setPageIndex(0);
        } else if (newData?.length > 0) {
          setTableData((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewData = newData.filter(
              (item) => !existingIds.has(item.id)
            );
            return [...prev, ...uniqueNewData];
          });
        }

        const totalRecords = newData[0]?.total_Count || 0;
        const currentTotalItems = isNewRequest
          ? newData.length
          : tableData.length + newData.length;
        setHasMore(currentTotalItems < totalRecords);

        if (newData.length === 0 && isNewRequest) {
          setHasMore(false);
        }

        return result?.data;
      } catch (error: any) {
        message.warning(error?.response?.data?.Message);
        setHasMore(false);
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [pathname, pageSize, tableData.length]
  );

  const { isLoading, refetch } = useQuery(
    [BALANCES, pathname, debouncedPartnerName],
    () => fetchData(0, true, debouncedPartnerName),
    {
      onSuccess: (data) => {
        // Check if we need to highlight a partner after data is loaded
        const highlightPartner = localStorage.getItem('highlightPartner');
        const scrollToPartner =
          localStorage.getItem('scrollToPartner') === 'true';
        const timestamp = localStorage.getItem('highlightTimestamp');

        // Only process if timestamp is recent (within last 10 seconds)
        const isRecent = timestamp && Date.now() - parseInt(timestamp) < 10000;

        if (
          highlightPartner &&
          scrollToPartner &&
          isRecent &&
          data?.data?.length > 0
        ) {
          // Remove horizontal scroll first
          setTimeout(() => {
            const tableContainer = tableContainerRef.current;
            if (tableContainer) {
              tableContainer.scrollLeft = 0;
            }
          }, 100);

          setTimeout(() => {
            const targetPartner = data.data.find(
              (partner: any) =>
                partner.name.toLowerCase().trim() ===
                highlightPartner.toLowerCase().trim()
            );

            if (targetPartner) {
              setHighlightedPartnerId(targetPartner.id);

              setTimeout(() => {
                const highlightedElement = document.querySelector(
                  `[data-partner-id="${targetPartner.id}"]`
                ) as HTMLElement;
                if (highlightedElement) {
                  // Get the table container
                  const tableContainer = tableContainerRef.current;
                  if (tableContainer) {
                    // Calculate element position relative to container
                    const containerRect =
                      tableContainer.getBoundingClientRect();
                    const elementRect =
                      highlightedElement.getBoundingClientRect();

                    // Calculate scroll position to center the element
                    const scrollTop =
                      tableContainer.scrollTop +
                      (elementRect.top - containerRect.top) -
                      containerRect.height / 2;

                    // Smooth scroll to the calculated position
                    tableContainer.scrollTo({
                      top: Math.max(0, scrollTop),
                      left: 0,
                      behavior: 'smooth',
                    });
                  }
                }
              }, 800); // Increase delay to ensure table is fully rendered

              setTimeout(() => {
                setHighlightedPartnerId(null);
              }, 5000);

              localStorage.removeItem('highlightPartner');
              localStorage.removeItem('scrollToPartner');
              localStorage.removeItem('highlightTimestamp');
            }
          }, 200);
        } else if (!isRecent) {
          // Clear old localStorage data
          localStorage.removeItem('highlightPartner');
          localStorage.removeItem('scrollToPartner');
          localStorage.removeItem('highlightTimestamp');
        }
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled: true,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  useEffect(() => {
    setTableData([]);
    setPageIndex(0);
    setHasMore(true);
    setHighlightedPartnerId(null); // Clear highlight when changing routes

    // Reset fetching flag when changing routes
    isFetchingRef.current = false;

    // Reset horizontal scroll when pathname changes
    setTimeout(() => {
      const tableContainer = tableContainerRef.current;
      if (tableContainer) {
        tableContainer.scrollLeft = 0;
        tableContainer.scrollTop = 0; // Also reset vertical scroll
      }
    }, 100);
  }, [pathname]);

  const handleScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMore ||
      isLoading
    )
      return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible (with some buffer)
    if (loadingRect.top <= containerRect.bottom + 50) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchData(nextPage, false, debouncedPartnerName);
    }
  }, [pageIndex, hasMore, isLoading, fetchData, tableData.length]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <>
      {open && <Statistics />}
      <div className="grid grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
        <div className="col-span-2 h-full">
          <DashboardHeader
            open={open}
            handleOpen={handleOpen}
            transactionType={transactionType}
            setPartnerName={setPartnerName}
            partnerName={partnerName}
          />
          <ScrollContainer ref={tableContainerRef}>
            <TableWrapper>
              <StyledTable
                columns={dynamicColumns}
                dataSource={tableData}
                loading={isLoading && pageIndex === 0}
                rowKey="id"
                pagination={false}
                size="small"
                rowClassName={(record: any, index: number) => {
                  const baseClass =
                    index % 2 === 0 ? 'bg-gray-100' : 'bg-white';
                  const highlightClass =
                    highlightedPartnerId === record.id ? 'highlighted-row' : '';
                  return `${baseClass} ${highlightClass}`;
                }}
                scroll={{ x: 'max-content' }}
                sticky={{
                  offsetHeader: 0,
                }}
              />
            </TableWrapper>

            <LoadingIndicator ref={loadingRef}>
              {isLoadingMore && <Spin size="small" />}
              {!hasMore && tableData.length > 0 && (
                <div style={{ color: '#999', marginTop: '8px' }}>
                  {translation.messages.allData}
                </div>
              )}
            </LoadingIndicator>
          </ScrollContainer>
        </div>
        <TransactionBalance />
      </div>
      <AddNewPartnerForm
        open={addCustomerFormOpen}
        handleClose={() => {
          setAddCustomerFormOpen(false);
          setPartnerToEdit(null);
        }}
        partnerToEdit={partnerToEdit || undefined}
        onPartnerChange={() => {
          setTableData([]);
          setPageIndex(0);
          setHasMore(true);
          refetch();
        }}
        isCreditor={pathname.includes('credit')}
        disabled
      />
    </>
  );
};

const ScrollContainer = styled.div`
  overflow: auto;
  position: relative;
  width: 100%;
  height: calc(100vh - 250px);
`;

const TableWrapper = styled.div`
  .ant-table-thead > tr > th {
    position: sticky !important;
    top: 0 !important;
    z-index: 10 !important;
    background: #f6f6f6 !important;
    color: #466afb !important;
    font-size: 16px;
    font-weight: semi-bold;
    opacity: 1 !important;
  }

  .ant-table-thead > tr {
    height: 50px !important;
  }

  .ant-table-tbody > tr {
    transition: background-color 0.2s ease;

    &.highlighted-row {
      background: linear-gradient(135deg, #ffeaa7, #fab1a0) !important;
    }
  }

  .ant-table-cell-ellipsis {
    padding-left: 24px !important;
  }

  .ant-table {
    .ant-table-cell {
      border-bottom: 1px solid rgba(70, 106, 251, 0.12);
    }
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #dbeafe !important; /* soft blue */
  }
`;

const StyledTable = styled(Table)`
  .ant-spin-container {
    background: transparent;
  }
` as typeof Table;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
`;

export default Dashboard;
